import './login.scss';

import type { PageProps } from 'keycloakify/login/pages/PageProps';
import { clsx } from 'keycloakify/tools/clsx';
import React, { useState } from 'react';

import { ReactComponent as EyeHide } from '../assets/hide-icon.svg';
import { ReactComponent as EyeShow } from '../assets/show-icon.svg';
import type { KcContext } from '../kcContext';
import Template, { kcProps } from '../Template';
import { i18n as fallbackI18n } from './../i18n';

export default function Login(
  props: Omit<
    PageProps<Extract<KcContext, { pageId: 'login.ftl' }>, any>,
    'Template' | 'doUseDefaultCss'
  >
) {
  const { kcContext, i18n } = props;

  const {
    social = { displayInfo: false },
    realm,
    url,
    login,
    auth,
    registrationDisabled,
  } = kcContext;

  const { msg, msgStr } = i18n ?? fallbackI18n;

  const usernameEditDisabled: boolean = (kcContext as any).usernameEditDisabled ?? false;

  const [isLoginButtonDisabled, setIsLoginButtonDisabled] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState('');

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Depending on the environment, the registration and forgot password links are different, the env is set in the package.json scripts.
  const envSpecificUrls = {
    registerUrl: '',
    forgotPasswordUrl: '',
  };

  if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'acc') {
    envSpecificUrls.registerUrl =
      'https://next.ggzecademy.nl/registration_1?register_username=' + registrationEmail;
    envSpecificUrls.forgotPasswordUrl = 'https://next.ggzecademy.nl/resetting/request';
  } else {
    envSpecificUrls.registerUrl =
      'https://portaal.ggzecademy.nl/registration_1?register_username=' + registrationEmail;
    envSpecificUrls.forgotPasswordUrl = 'https://portaal.ggzecademy.nl/resetting/request';
  }

  return (
    <Template
      kcContext={kcContext}
      i18n={i18n}
      doUseDefaultCss={true}
      displayInfo={social.displayInfo}
      headerNode={msg('doLogIn')}
    >
      <>
        <div className="intro">
          <h1>Log in en start met leren</h1>
          <p>
            GGZ Ecademy biedt een online leerplatform: het GGZ Ecademy Portaal. Log in of maak een
            account aan.{' '}
          </p>
        </div>
        <div
          id="kc-form"
          className={clsx(
            realm.password && social.providers !== undefined && kcProps.kcContentWrapperClass
          )}
        >
          <div
            id="kc-form-wrapper"
            className={clsx(
              realm.password &&
                social.providers && [
                  kcProps.kcFormSocialAccountContentClass,
                  kcProps.kcFormSocialAccountClass,
                ]
            )}
          >
            {realm.password && (
              <form
                id="kc-form-login"
                onSubmit={() => {
                  setIsLoginButtonDisabled(true);
                  return true;
                }}
                action={url.loginAction}
                method="post"
              >
                <h2>Inloggen</h2>

                <div className={clsx(kcProps.kcFormGroupClass)}>
                  {(() => {
                    return (
                      <>
                        <label htmlFor="username" className={clsx(kcProps.kcLabelClass)}>
                          {msg(realm.loginWithEmailAllowed ? 'usernameOrEmail' : 'username')}
                        </label>

                        <input
                          id="username"
                          className={clsx(kcProps.kcInputClass)}
                          name="username"
                          defaultValue={login.username ?? ''}
                          placeholder="E-mailadres"
                          type="text"
                          {...(usernameEditDisabled
                            ? { disabled: true }
                            : { autoFocus: true, autoComplete: 'off' })}
                        />
                      </>
                    );
                  })()}
                </div>
                <div className={clsx(kcProps.kcFormGroupClass)}>
                  <label htmlFor="password" className={clsx(kcProps.kcLabelClass)}>
                    {msg('password')}
                  </label>
                  <div className="password-container">
                    <input
                      id="password"
                      placeholder="Wachtwoord"
                      className={clsx(kcProps.kcInputClass)}
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="off"
                    />
                    <button className="password-toggler" onClick={togglePassword} type="button">
                      {showPassword ? <EyeHide /> : <EyeShow />}
                    </button>
                  </div>
                </div>
                <div className="form-options">
                  <div id="kc-form-options"></div>
                  {realm.resetPasswordAllowed && (
                    <a className="forgot-password" href={envSpecificUrls.forgotPasswordUrl}>
                      {msg('doForgotPassword')}
                    </a>
                  )}
                </div>
                <div id="kc-form-buttons" className={clsx(kcProps.kcFormGroupClass)}>
                  <input
                    type="hidden"
                    id="id-hidden-input"
                    name="credentialId"
                    {...(auth?.selectedCredential !== undefined
                      ? {
                          value: auth.selectedCredential,
                        }
                      : {})}
                  />
                  <input
                    className={clsx(
                      kcProps.kcButtonClass,
                      kcProps.kcButtonPrimaryClass,
                      kcProps.kcButtonBlockClass,
                      kcProps.kcButtonLargeClass
                    )}
                    name="login"
                    id="kc-login"
                    type="submit"
                    value={msgStr('doLogIn')}
                  />
                </div>
              </form>
            )}
          </div>
        </div>
        {realm.password && realm.registrationAllowed && !registrationDisabled && (
          <form id="kc-registration" action={envSpecificUrls.registerUrl}>
            <label htmlFor="register_username" className="hidden">
              Email address
            </label>
            <h2>Account aanmaken</h2>
            <input
              type="email"
              id="register_username"
              name="register_username"
              className="form-control"
              placeholder="E-mailadres"
              defaultValue=" "
              value={registrationEmail}
              onChange={e => setRegistrationEmail(e.target.value)}
              required
            />
            <div className="bottom">
              <button id="register" className="btn btn-secondary" type="submit">
                Ga verder
              </button>
            </div>
          </form>
        )}
      </>
    </Template>
  );
}
